/**
 * Remonter du planning à la source : le vocabulaire commun des deux sauts.
 *
 * *Demande de Chris, 17/09/2026 : « dans le planning des ressources il faut pouvoir cliquer sur
 * les heures et que cela ouvre le plan de charge et que cela positionne sur la saisie dans le
 * plan de charge. Et quand on arrive dans le plan de charge il faut pouvoir cliquer sur la ligne
 * et ouvrir en mode planning la contre-étude et arriver sur la ligne concernée. Comme cela à
 * partir du planning on peut remonter à la source à modifier éventuellement directement. »*
 *
 * ## Deux sauts, une seule chaîne
 *
 * Planning des ressources → plan de charge → contre-étude en mode planning. Le premier saut
 * traverse **deux fenêtres** (le planning des ressources est une vraie fenêtre du navigateur,
 * voir `PlanningRessources`), le second reste dans la même. D'où ce module : les deux extrémités
 * de chaque saut doivent nommer la même chose de la même façon, et une clé calculée à un endroit
 * et recalculée à l'autre finit toujours par diverger d'un champ.
 *
 * ## Ce que le saut désigne, et ce qu'il ne désigne pas
 *
 * **Une case du planning des ressources peut porter plusieurs occupations** — une personne sur
 * deux affaires le même jour, c'est exactement ce que la colonne « conflits » compte. Le saut ne
 * choisit donc pas pour le lecteur : il emporte *toutes* les occupations de la case, le plan de
 * charge les surligne toutes, et se positionne sur la première. Choisir silencieusement l'une des
 * deux aurait envoyé sur la mauvaise affaire un jour sur deux, sans rien dire.
 *
 * C'est aussi pourquoi `chantierId` vaut `null` quand la case en porte plusieurs : le filtre
 * d'affaire du plan de charge s'ouvre alors sur « toutes », sinon la moitié de ce qu'on vient
 * de cliquer serait masquée à l'arrivée.
 */

/** Le type du message qui traverse les deux fenêtres. Vérifié à la réception. */
export const MESSAGE_ALLER_AU_PLAN = "planif:aller-au-plan-de-charge";

/** De quel côté de la case « transport » le planning des ressources regardait. */
export type Vue = "exploitation" | "transport";

/**
 * Ce qu'une occupation cliquée désigne dans le plan de charge.
 *
 * `ligneId` est nul pour un besoin qui n'en porte pas ; le numéro de poste prend alors le
 * relais dans la clé. Les deux ensemble, plutôt que l'un ou l'autre : une ligne retirée de la
 * contre-étude perd son identifiant sans que le poste bouge.
 */
export type OccupationCiblee = {
  readonly chantierId: string;
  readonly ligneId: string | null;
  readonly poste: string;
  readonly rang: number;
  readonly nature: "heures" | "transport" | "livraison";
};

/** Ce que le planning des ressources envoie au plan de charge. */
export type CibleDuPlanDeCharge = {
  readonly jour: string;
  readonly vue: Vue;
  /** L'affaire sur laquelle filtrer, ou `null` quand la case en porte plusieurs. */
  readonly chantierId: string | null;
  readonly occupations: readonly OccupationCiblee[];
};

/** Ce que le plan de charge demande au planning de la contre-étude d'ouvrir. */
export type CibleDeLaLigne = {
  readonly poste: string;
  readonly rang: number;
};

/**
 * La clé d'un besoin dans le plan de charge.
 *
 * **Elle ne porte pas la pastille**, contrairement à la clé React des rangées : deux échéances
 * de la même ligne le même jour sont deux rangées distinctes, mais une seule source à remonter.
 * Le saut les surligne donc toutes les deux — c'est bien la même ligne de contre-étude qu'on
 * vient de cliquer, et n'en désigner qu'une ferait chercher l'autre.
 */
export function cleDuBesoin(besoin: {
  readonly chantierId: string;
  readonly ligneId: string | null;
  readonly poste: string;
  readonly rang: number;
  readonly nature: "heures" | "transport" | "livraison";
}): string {
  return [
    besoin.chantierId,
    besoin.ligneId ?? besoin.poste,
    String(besoin.rang),
    besoin.nature,
  ].join("#");
}

/**
 * L'attribut qui marque une rangée atteignable par un saut.
 *
 * **Un attribut de données, et non un `id`.** Deux échéances de la même ligne le même jour sont
 * deux rangées qui portent la même clé : des `id` en double sont un document invalide, et
 * `getElementById` en choisirait une sans le dire. Un attribut se répète légitimement, et
 * `querySelector` annonce qu'il rend la première.
 */
export const ATTRIBUT_CIBLE = "data-cible";

/** La valeur d'ancre d'une rangée du plan de charge, pour le jour visé. */
export function ancreDuPlan(jour: string, cle: string): string {
  return `plan#${jour}#${cle}`;
}

/**
 * Les ancres que le plan de charge doit surligner, pour une cible donnée.
 *
 * **Elles portent le JOUR, et c'est tout l'objet de cette fonction.** Une ligne de contre-étude
 * travaille cinq jours d'affilée : le plan de charge la répète sous chaque date. Surligner sur la
 * seule clé du besoin les allumait toutes — mesuré au navigateur le 17/09/2026, deux journées
 * marquées pour une case cliquée. Le lecteur y lit « voici les endroits concernés » alors qu'il
 * en a désigné un seul, et le repère cesse de repérer.
 */
export function ancresDeLaCible(cible: CibleDuPlanDeCharge): ReadonlySet<string> {
  return new Set(cible.occupations.map((o) => ancreDuPlan(cible.jour, cleDuBesoin(o))));
}

/** La valeur d'ancre d'une ligne de ressource dans le planning de la contre-étude. */
export function ancreDeLaLigne(poste: string, rang: number): string {
  return `gantt#${poste}#${String(rang)}`;
}

/**
 * Amener l'ancre sous les yeux.
 *
 * Le défilement seul ne suffit pas à dire *laquelle* : sur un plan de charge de trois mois, la
 * rangée arrive au milieu de deux cents autres qui lui ressemblent. C'est la classe `cible`,
 * posée par l'écran, qui la désigne — et elle reste, parce qu'une surbrillance qui s'efface au
 * bout de deux secondes a disparu le temps qu'on lève les yeux de la souris.
 *
 * Rend `false` quand la rangée n'est pas encore là : le plan se recharge, l'onglet bascule, et
 * l'appelant rappelle cette fonction à l'étape suivante. Ce n'est pas un défaut, c'est l'état
 * normal des premiers passages.
 */
export function amenerSousLesYeux(ancre: string): boolean {
  const element = document.querySelector(`[${ATTRIBUT_CIBLE}="${CSS.escape(ancre)}"]`);
  if (element === null) return false;
  element.scrollIntoView({ block: "center", behavior: "smooth" });
  return true;
}
